import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import type { AgentsBoard } from '../../api/agentSchemas';
import { usePermissions } from '../../auth/usePermissions';
import { spoken, type AgentLine } from './agentLine';
import { answer } from './conversation';
import { loadFinanceFacts, type FinanceFacts } from './finance/facts';
import { financeNeedsFor } from './replies';
import { loadRouterModel } from './router/model';
import { routeQuestion } from './router/route';

/** How long a question waits for the router's chunk: past this the team answers by keyword instead of leaving the stage thinking. */
export const MODEL_WAIT_MS = 3000;

async function waitForModel(): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const limit = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, MODEL_WAIT_MS);
  });
  try {
    await Promise.race([loadRouterModel(), limit]);
  } finally {
    clearTimeout(timer);
  }
}

export type Asker = {
  /** The owner may read the numbers of the business; the chips and the answers follow. */
  canSeeMoney: boolean;
  /** The team's reply to something typed, with the numbers fetched first when the question is about money. */
  reply: (text: string) => Promise<AgentLine>;
};

export function useAnswer(board: AgentsBoard): Asker {
  const client = useQueryClient();
  const canSeeMoney = usePermissions().can('finanzas');

  // The router's model is its own chunk: start fetching it as soon as someone can talk to the team.
  useEffect(() => {
    void loadRouterModel();
  }, []);

  const reply = useCallback(
    async (text: string): Promise<AgentLine> => {
      try {
        await waitForModel();
        const needs = financeNeedsFor(routeQuestion(text).intent);
        if (!needs) return answer(text, board);
        if (!canSeeMoney) return answer(text, board, { status: 'locked' });
        // Only a failed fetch of the numbers is "no pude sacar las cuentas": answer() says it for an error.
        const facts = await loadFinanceFacts(client, needs, new Date()).catch((error: unknown): FinanceFacts => {
          console.error('The numbers of the business could not be loaded.', error);
          return { status: 'error' };
        });
        return answer(text, board, facts);
      } catch (error) {
        console.error('The team could not answer a question.', error);
        return spoken('direccion', 'nosabe');
      }
    },
    [board, canSeeMoney, client],
  );

  return { canSeeMoney, reply };
}
